import { Inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WINDOW } from '@lsq-frontend/angular-shared/settings';
import { ZoneAwareStream } from '../../models/common';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

export interface WindowDimensions {
    width: number;
    height: number;
}

@Injectable({ providedIn: 'root' })
export class WindowReferenceService {
    private resizeSubject = new BehaviorSubject<WindowDimensions>({
        width: this._window.innerWidth,
        height: this._window.innerHeight
    });
    public resizeStream: Observable<WindowDimensions>;

    private scrollStream = fromEvent(this._window, 'scroll').pipe(
        map(() => ({ scrollX: this._window.scrollX, scrollY: this._window.scrollY }))
    );

    public bodyClickEventsStream = merge(
        fromEvent<Event>(this._window.document.body, 'mousedown'),
        fromEvent<Event>(this._window.document.body, 'click'),
        fromEvent<Event>(this._window.document.body, 'mouseup')
    );

    constructor(
        private _ngZone: NgZone,
        @Inject(WINDOW) private _window: Window & typeof globalThis
    ) {
        SingletonServiceGuard.register('WindowReferenceService');
        this.resizeStream = this.resizeSubject.asObservable();

        fromEvent(this._window, 'resize').subscribe(() => {
            this.resizeSubject.next({
                width: this._window.innerWidth,
                height: this._window.innerHeight
            });
        });
    }

    // Download the given blob using the window object.
    // https://stackoverflow.com/questions/46878484/angular-4-3-getting-an-arraybuffer-with-new-httpclient
    public downloadFile(blob: any, filename: string): string {
        const url = this._window.URL.createObjectURL(blob);

        // IE does not support opening blobs in some ways
        // https://stackoverflow.com/questions/18755750/saving-text-in-a-local-file-in-internet-explorer-1
        // Create hidden dom element (so it works in all browsers)
        const a = this._window.document.createElement('a');
        a.setAttribute('style', 'display:none;');
        this._window.document.body.appendChild(a);

        // Create file, attach to hidden element and open hidden element
        a.href = url;
        a.download = filename;
        a.click();

        return url;
    }

    // Gets the cookie with the given name.
    // https://stackoverflow.com/questions/34298133/angular-cookies
    public getCookie(name: string): string {
        const cookieArray: Array<string> = this._window.document.cookie.split(';');
        const cookieLength: number = cookieArray.length;
        const cookieName = `${name}=`;
        let cookie: string;

        for (let i = 0; i < cookieLength; i += 1) {
            cookie = cookieArray[i].replace(/^\s+/g, '');
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }

        return '';
    }

    // Goes to an external page.
    public goToExternalPage(url: string): void {
        this._window.location.href = url;
    }

    // Gets an item from local storage.
    public getItemFromLocalStorage<ItemType = object | string | undefined>(key: string): ItemType {
        const item = localStorage.getItem(key);

        if (item && (item[0] === '{' || item[0] === '[')) {
            try {
                return JSON.parse(item);
            } catch (error) {
                return undefined;
            }
        }

        return (item as any) || undefined;
    }

    // Sets the item into local storage.
    public setItemInLocalStorage(key: string, item: object | string): void {
        if (typeof item === 'string') {
            localStorage.setItem(key, item);
        } else if (item == null) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, JSON.stringify(item));
        }
    }

    public scrollTo(x: number, y: number, smooth?: boolean): void {
        if (smooth) {
            this._window.scrollTo({
                top: y,
                left: x,
                behavior: 'smooth'
            });
        } else {
            this._window.scroll(x, y);
        }
    }

    public scrollToTop(): void {
        this._window.scroll(0, 0);
    }

    public createScrollStream(
        runAlways?: boolean
    ): ZoneAwareStream<{ scrollX: number; scrollY: number }> {
        return new ZoneAwareStream<{ scrollX: number; scrollY: number }>(
            this._ngZone,
            this.scrollStream,
            runAlways
        );
    }

    public getOriginUrl(): string {
        return this._window.location.origin;
    }

    public vibrate(vibratePattern: number | number[]): boolean {
        return (
            typeof this._window.navigator.vibrate === 'function' &&
            this._window.navigator.vibrate(vibratePattern)
        );
    }

    public hardReload(): void {
        this._window.location.href = this._window.location.origin;
    }
}
